import dayjs from "dayjs";
import axios from "axios";
import React, { useState, useEffect, Suspense, lazy } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom"; // <--- ADD THIS

import {
  Box,
  IconButton,
  Container,
  Paper,
  Stack,
  TextField,
  Autocomplete,
  Divider,
  Skeleton,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";

import { useSnackBar } from "context/snackbarContext";
import PosDrawer from "../components/PosDrawer";
import CommonBackdrop from "loader/CommonBackdrop";

import { setAllProducts } from "../slice/posItemsSlice";
import { updateOrder } from "../slice/posSlice";
import { setPosProducts } from "../slice/posProducts";

import { baseUrl, redirectUrl } from "config";

// Lazy load these components
const ProductList = lazy(() => import("./components/ProductList"));
const FilterAndBillNo = lazy(() => import("./components/FilterAndBillNo"));
const Categories = lazy(() => import("./components/Categories"));
const Footer = lazy(() => import("./components/Footer"));

// ------- APIs -------//
const GET_CUSTOMER = "/getMasterDataPos";
const GET_MAX_BILL = "/getMaxBill";
const GET_CUSTOMER_BALANCE = "/getPartynameCurrentBalance";

// Styled components for better visual separation and theming
const HeaderPaper = styled(Paper)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(2, 0),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: theme.shape.borderRadius,
  position: "sticky",
  top: 0,
  zIndex: theme.zIndex.appBar + 2,
  boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
}));

const HomeButton = styled(IconButton)(({ theme }) => ({
  color: "inherit",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderRadius: "50%",
  },
}));

// Styled component for the sticky Categories and Filter section
const StickySectionPaper = styled(Paper)(({ theme }) => ({
  position: "sticky",
  top: theme.spacing(9),
  zIndex: theme.zIndex.appBar + 1,
  paddingBottom: theme.spacing(1),
  marginBottom: theme.spacing(1),
  boxShadow: "0 0px 0px rgba(0,0,0,0.05)",
}));

const PosOrder = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackBar();

  // redux state
  const allProducts = useSelector((state) => state.ProductsData?.data?.result);
  const posProducts = useSelector((state) => state.posProducts);
  const productsStore = useSelector((state) => state.posItems);
  const headerSlice = useSelector((state) => state.posSlice);
  const commonState = useSelector((state) => state.common);

  const { billNo } = location.state || {};

  // states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [customersList, setCustomersList] = useState([]);
  const [currentItems, setCurrentItems] = useState([]);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [maxBill, setMaxBill] = useState(0);
  const [batchNo, setBatchNo] = useState(0);
  // const [isCustomerBalanceLoading, setIsCustomerBalanceLoading] =
  //   useState(false);

  // Fetch category and customersList
  useEffect(() => {
    const fetchData = async () => {
      try {
        const customers = await axios.get(baseUrl + GET_CUSTOMER, {
          withCredentials: true,
        });

        if (customers.data?.data?.[0]) {
          setCustomersList(customers.data?.data?.[0]);
        }

        if (customers.data?.data?.[1]) {
          const updateCategories = [
            { Typ: "ALL", Description: "All Products", Nam: null },
            ...customers.data?.data?.[1],
          ];
          setCategories(updateCategories);
        }

        if (customers.data?.data?.[2][0] && !billNo) {
          setBatchNo(customers.data?.data?.[2][0]?.batchno);
        }
      } catch (err) {
        if (err.response?.status === 403 || err.response?.status === 401) {
          redirectUrl();
        } else {
          showSnackbar("error", "Failed to load data.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (batchNo) {
      getMaxBillAndOrderNo(batchNo);
    }
    if (headerSlice.isRestaurant) {
      const cash = customersList.find((cust) => cust.Name === "CASH");
      dispatch(
        updateOrder({
          customer: cash,
        })
      );
    }else{
       dispatch(
        updateOrder({
          customer: null,
        })
      );
    }
    dispatch(
      updateOrder({
        tax: commonState.billWiseTax || 0,
      })
    );
  }, [headerSlice.refetch, customersList]);

  // To ensure products fetched successfully
  useEffect(() => {
    if (allProducts && allProducts.length > 0) {
      const updatedProducts = allProducts.map((product) => ({
        ...product,
        total: 0,
        taxAmt: 0,
        unit: product.Typ,
        batch: [],
        rate: product.Rate1 || 0,
        qty:
          productsStore.find((item) => item.ItemNo === product.ItemNo)?.qty ||
          0,
      }));
      dispatch(setPosProducts(updatedProducts));
      dispatch(setAllProducts(updatedProducts));
    }
  }, [allProducts, headerSlice.refetch, headerSlice.billNo]);

  // get customer balance amount
  useEffect(() => {

    const getCustomerBalance = async () => {
      try {
        const res = await axios.post(
          baseUrl + GET_CUSTOMER_BALANCE,
          { date: dayjs(), name: headerSlice?.customer?.Name },
          { withCredentials: true }
        );

        if (res.data?.[0][0]?.balance) {
          dispatch(
            updateOrder({
              balance: res.data?.[0][0]?.balance,
            })
          );
        } else {
          showSnackbar("error", "Failed to load customer balance.");
        }
      } catch (err) {
        if (err.response?.status === 403 || err.response?.status === 401) {
          redirectUrl();
        } else {
          showSnackbar("error", "Failed to load data.");
        }
      }
    };
    if (headerSlice?.customer?.Name && !headerSlice.isRestaurant ) {
      getCustomerBalance();
    }
  }, [headerSlice.customer]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category.Typ);

    if (category.Typ === "ALL") {
      dispatch(setAllProducts(posProducts));
    } else {
      const filteredItems = posProducts.filter(
        (item) => item.Size === category.Typ
      );
      dispatch(setAllProducts(filteredItems));
    }
  };

  // get max bill and order no
  const getMaxBillAndOrderNo = async (batchNo) => {
    try {
      setIsLoading(true);
      const maxBill = await axios.post(
        baseUrl + GET_MAX_BILL,
        { BatchNo: batchNo },
        {
          withCredentials: true,
        }
      );
      if (maxBill?.data?.[0]) {
        dispatch(
          updateOrder({
            billNo: maxBill?.data?.[0]?.BillNo,
            orderNo: maxBill?.data?.[0]?.OrderNo,
          })
        );
        setMaxBill(maxBill?.data?.[0]?.BillNo);
        setIsLoading(false);
      }
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 401) {
        redirectUrl();
      }
    }
  };

  // handle customer change
  const handleParty = async (e, value) => {
    dispatch(updateOrder({ customer: value }));
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {/* Common Backdrop for full-page loading (e.g., initial fetch, bill number fetch) */}
      <CommonBackdrop open={false} />

      {/* Header */}
      <HeaderPaper elevation={4}>
        <Stack direction="row" alignItems="center">
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{ color: "inherit" }}
          >
            <MenuIcon />
          </IconButton>
          {isLoading ? (
            <Skeleton
              variant="rectangular"
              width={340}
              height={40}
              sx={{ borderRadius: 1 }}
            />
          ) : (
            <Autocomplete
              disablePortal
              id="dealer-autocomplete"
              color="error"
              options={customersList}
              sx={{ width: { xs: 220, sm: 280, md: 340 } }}
              getOptionLabel={(option) => option?.Name || ""}
              onChange={(e, newVal) => handleParty(e, newVal)}
              fullWidth
              value={headerSlice.customer}
              size="small"
              renderInput={(params) => (
                <TextField
                  color="secondary"
                  {...params}
                  required
                  placeholder="Choose customer here"
                  variant="outlined"
                />
              )}
            />
          )}
        </Stack>

        <HomeButton
          onClick={() =>
            navigate(headerSlice.isRestaurant ? "/pos/section" : "/")
          }
        >
          <HomeIcon />
        </HomeButton>
      </HeaderPaper>

      <Container maxWidth="md" disableGutters sx={{ mt: 1 }}>
        {/* Drawer (no skeleton needed here as it's typically shown after interaction) */}
        <PosDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          customersList={customersList}
          categories={categories}
          onCategoryClick={handleCategoryClick}
        />

        {/* Sticky Category Chips and Filter Section */}
        <StickySectionPaper elevation={2}>
          {/* Category Chips */}
          <Suspense
            fallback={
              <Stack direction="row" spacing={1} sx={{ p: 1 }}>
                <Skeleton
                  variant="rectangular"
                  width={80}
                  height={32}
                  sx={{ borderRadius: 16 }}
                />
                <Skeleton
                  variant="rectangular"
                  width={100}
                  height={32}
                  sx={{ borderRadius: 16 }}
                />
                <Skeleton
                  variant="rectangular"
                  width={70}
                  height={32}
                  sx={{ borderRadius: 16 }}
                />
              </Stack>
            }
          >
            <Categories
              handleCategoryClick={handleCategoryClick}
              selectedCategory={selectedCategory}
              categories={categories}
            />
          </Suspense>
          <Divider sx={{ my: 1 }} />

          {/* Filter and BillNo */}
          <Suspense
            fallback={
              <Stack direction="row" spacing={2} sx={{ p: 1 }}>
                <Skeleton variant="rectangular" width="40%" height={40} />
                <Skeleton variant="rectangular" width="30%" height={40} />
                <Skeleton variant="rectangular" width="30%" height={40} />
              </Stack>
            }
          >
            <FilterAndBillNo
              maxBill={maxBill}
              customersList={customersList}
              setCurrentItems={setCurrentItems}
              setSelectedCategory={setSelectedCategory}
            />
          </Suspense>
        </StickySectionPaper>

        {/* Item List */}
        <Paper
          sx={{
            borderRadius: 1,
            position: "relative",
            minHeight: { xs: "calc(100vh - 400px)", sm: "calc(100vh - 350px)" },
            pb: "150px",
            px: { xs: 0.5, sm: 1, md: 2 },
            // mt: 2,
          }}
        >
          {/* product list */}
          <Suspense
            fallback={
              <Box sx={{ p: 2 }}>
                {[...Array(6)].map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="rectangular"
                    height={80}
                    sx={{ my: 1, borderRadius: 1 }}
                  />
                ))}
              </Box>
            }
          >
            <ProductList />
          </Suspense>

          {/* footer section */}
          <Suspense
            fallback={
              <Box
                sx={{ p: 2, position: "absolute", bottom: 0, width: "100%" }}
              >
                <Skeleton
                  variant="rectangular"
                  height={100}
                  sx={{ borderRadius: 1 }}
                />
              </Box>
            }
          >
            <Footer
              setSelectedCategory={setSelectedCategory}
              currentItems={currentItems}
            />
          </Suspense>
        </Paper>
      </Container>
    </Box>
  );
};

export default PosOrder;
